import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Calendar,
  Link as LinkIcon,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Copy,
  X,
  Users
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const CalendarSyncSettings = ({ open, onClose, businessId }) => {
  const { _user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncs, setSyncs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [icalUrl, setIcalUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [calendarScope, setCalendarScope] = useState('all'); // 'all' or 'individual'
  const [selectedStaffMember, setSelectedStaffMember] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && businessId) {
      fetchSyncs();
      fetchStaffMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, businessId]);

  const fetchStaffMembers = async () => {
    try {
      const response = await api.get(`/api/business-members/business/${businessId}`);
      const members = response.data.members || [];
      setStaffMembers(members.filter(m => m.status === 'active'));
    } catch (error) {}
  };

  const fetchSyncs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/calendar-sync/business/${businessId}`);
      setSyncs(response.data.syncs || []);
      
      // Find iCal sync and generate URL if needed
      const icalSync = response.data.syncs?.find(s => s.syncType === 'ical' && s.isEnabled);
      if (icalSync) {
        // We'll need to get the URL from setup endpoint
        const setupResponse = await api.post('/api/calendar-sync/ical/setup', {
          businessId,
          isEnabled: true
        });
        setIcalUrl(setupResponse.data.icalUrl);
      }
    } catch (error) {
      setError('Failed to load calendar sync settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const response = await api.get('/api/calendar-sync/google/auth', {
        params: { businessId }
      });
      // Open Google OAuth in new window
      window.location.href = response.data.authUrl;
    } catch (error) {
      setError('Failed to connect Google Calendar');
    }
  };

  const handleICalSetup = async () => {
    if (calendarScope === 'individual' && !selectedStaffMember) {
      setError('Please select a staff member first');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/calendar-sync/ical/setup', {
        businessId,
        isEnabled: true,
        syncPreferences: {
          syncConfirmed: true,
          syncPending: false,
          syncCancelled: false,
          syncPastBookings: true,
          syncFutureDays: 365,
          includeClientDetails: true,
          includeLocation: true,
          includeNotes: true
        },
        teamMemberId: calendarScope === 'individual' ? selectedStaffMember : null
      });
      setIcalUrl(response.data.icalUrl);
      setSuccess('iCal calendar URL generated successfully!');
      await fetchSyncs();
    } catch (error) {
      setError('Failed to generate iCal URL');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async (syncId, syncType) => {
    setSyncing(true);
    setError('');
    try {
      if (syncType === 'google') {
        await api.post('/api/calendar-sync/google/sync', { 
          businessId,
          teamMemberId: calendarScope === 'individual' ? selectedStaffMember : null
        });
        setSuccess('Google Calendar synced successfully!');
      }
      await fetchSyncs();
    } catch (error) {
      setError('Failed to sync calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleSync = async (syncId, enabled) => {
    setLoading(true);
    try {
      await api.put(`/api/calendar-sync/${syncId}`, {
        isEnabled: !enabled
      });
      await fetchSyncs();
      setSuccess(`Calendar sync ${!enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setError('Failed to update sync settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSync = async (syncId) => {
    if (!window.confirm('Are you sure you want to disable this calendar sync?')) {
      return;
    }
    setLoading(true);
    try {
      await api.delete(`/api/calendar-sync/${syncId}`);
      await fetchSyncs();
      setSuccess('Calendar sync disabled');
    } catch (error) {
      setError('Failed to disable sync');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const getStatusChip = (sync) => {
    if (!sync.isEnabled) {
      return <Chip label="Disabled" color="default" size="small" />;
    }
    switch (sync.lastSyncStatus) {
      case 'success':
        return <Chip label="Synced" color="success" size="small" icon={<CheckCircle size={16} />} />;
      case 'error':
        return <Chip label="Error" color="error" size="small" icon={<XCircle size={16} />} />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" icon={<Clock size={16} />} />;
      default:
        return <Chip label="Not synced" color="default" size="small" />;
    }
  };

  const googleSync = syncs.find(s => s.syncType === 'google');
  const icalSync = syncs.find(s => s.syncType === 'ical');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calendar size={24} weight="regular" />
            <Typography variant="h6">Sync Your Calendar</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          See your Idotoo bookings alongside your personal calendar. We give you a link that you paste into Apple Calendar, Outlook, or any calendar app that supports subscriptions. Your calendar then updates automatically when bookings change.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Calendar Scope Selection */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            <Users size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Select Calendar Scope
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Calendar Scope</InputLabel>
            <Select
              value={calendarScope}
              onChange={(e) => {
                setCalendarScope(e.target.value);
                if (e.target.value === 'all') {
                  setSelectedStaffMember('');
                }
              }}
              label="Calendar Scope"
            >
              <MenuItem value="all">All Staff</MenuItem>
              <MenuItem value="individual">Individual Staff Member</MenuItem>
            </Select>
          </FormControl>

          {calendarScope === 'individual' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel shrink={true}>Select Staff Member</InputLabel>
              <Select
                value={selectedStaffMember || ''}
                onChange={(e) => setSelectedStaffMember(e.target.value)}
                label="Select Staff Member"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === '') {
                    return 'Select a staff member...';
                  }
                  const selectedMember = staffMembers.find(m => m._id === selected);
                  return selectedMember 
                    ? `${selectedMember.user?.firstName || ''} ${selectedMember.user?.lastName || ''}`.trim()
                    : selected;
                }}
              >
                <MenuItem value="">
                  <em>Select a staff member...</em>
                </MenuItem>
                {staffMembers.map((member) => (
                  <MenuItem key={member._id} value={member._id}>
                    {member.user?.firstName} {member.user?.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {calendarScope === 'all' 
              ? 'Sync or download calendar for all staff members'
              : 'Sync or download calendar for the selected staff member only'}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Google Calendar Sync — hidden for now (not yet functional) */}
        {false && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={24} weight="regular" />
              <Typography variant="h6">Google Calendar Sync</Typography>
            </Box>
          </Box>
          
          {googleSync ? (
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusChip(googleSync)}
                  {googleSync.lastSyncAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last synced: {new Date(googleSync.lastSyncAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Sync Now">
                    <IconButton
                      size="small"
                      onClick={() => handleSyncNow(googleSync._id, 'google')}
                      disabled={syncing || !googleSync.isEnabled}
                    >
                      <Download size={18} />
                    </IconButton>
                  </Tooltip>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={googleSync.isEnabled}
                        onChange={() => handleToggleSync(googleSync._id, googleSync.isEnabled)}
                        size="small"
                      />
                    }
                    label={googleSync.isEnabled ? 'Enabled' : 'Disabled'}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSync(googleSync._id)}
                    color="error"
                  >
                    <X size={18} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<Calendar size={20} />}
              onClick={handleGoogleAuth}
              fullWidth
              disabled={calendarScope === 'individual' && !selectedStaffMember}
            >
              Connect Google Calendar
            </Button>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Sync your bookings directly to Google Calendar. Changes will be reflected automatically.
            {calendarScope === 'individual' && selectedStaffMember && ' Only bookings for the selected staff member will be synced.'}
          </Typography>
        </Box>
        )}

        {/* Calendar subscription (iCal/ICS) */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon size={24} weight="regular" />
              <Typography variant="h6">Subscribe from your calendar app</Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>How this works:</strong> Generate your personal calendar link below, copy it, then add it to Apple Calendar, Outlook, or any app that supports calendar subscriptions. Your Idotoo bookings will appear in that calendar automatically and refresh when things change.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>Apple Calendar (Mac):</strong> File → New Calendar Subscription → paste the link.<br />
              <strong>Apple Calendar (iPhone/iPad):</strong> Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → paste the link.<br />
              <strong>Outlook:</strong> Add calendar → Subscribe from web → paste the link.
            </Typography>
          </Alert>
          
          {/* iCal Subscription URL */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Subscription URL
            </Typography>
            
            {calendarScope === 'individual' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Subscription URLs are available for "All Staff" calendars only. Use the download option below to get an .ics file for individual staff members.
                </Typography>
              </Alert>
            ) : (
              <>
                {icalSync && icalUrl ? (
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      {getStatusChip(icalSync)}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={icalSync.isEnabled}
                            onChange={() => handleToggleSync(icalSync._id, icalSync.isEnabled)}
                            size="small"
                          />
                        }
                        label={icalSync.isEnabled ? 'Enabled' : 'Disabled'}
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      value={icalUrl}
                      label="Calendar URL"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <Tooltip title={copySuccess ? 'Copied!' : 'Copy URL'}>
                            <IconButton
                              onClick={() => copyToClipboard(icalUrl)}
                              edge="end"
                            >
                              <Copy size={18} />
                            </IconButton>
                          </Tooltip>
                        )
                      }}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Copy this link and paste it into your calendar app's "add subscription" option (see the instructions above).
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<LinkIcon size={20} />}
                    onClick={handleICalSetup}
                    fullWidth
                    disabled={loading}
                    sx={{ mb: 1 }}
                  >
                    Generate my calendar link
                  </Button>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  This link is private to you — don't share it.
                </Typography>
              </>
            )}
          </Box>

          {/* Calendar file download */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Or download a one-off calendar file
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A calendar file is a snapshot of your current bookings. Import it once into your calendar app — it won't update automatically. Use the subscription link above if you want automatic updates.
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<Download size={20} />}
              onClick={async () => {
                setDownloading(true);
                setError('');
                try {
                  const endpoint = calendarScope === 'all' 
                    ? '/api/calendar-sync/ical/download/main'
                    : `/api/calendar-sync/ical/download/staff/${selectedStaffMember}`;
                  
                  const response = await api.post(endpoint, {
                    businessId,
                    syncPreferences: {
                      syncConfirmed: true,
                      syncPending: false,
                      syncCancelled: false,
                      syncPastBookings: true,
                      syncFutureDays: 365,
                      includeClientDetails: true,
                      includeLocation: true,
                      includeNotes: true
                    }
                  }, {
                    responseType: 'blob'
                  });
                  
                  let filename;
                  if (calendarScope === 'all') {
                    filename = `main-calendar-${businessId}.ics`;
                  } else {
                    const selectedMember = staffMembers.find(m => m._id === selectedStaffMember);
                    const memberName = `${selectedMember?.user?.firstName || ''} ${selectedMember?.user?.lastName || ''}`.trim().replace(/\s+/g, '-');
                    filename = `calendar-${memberName}-${businessId}.ics`;
                  }
                  
                  const blob = new Blob([response.data], { type: 'text/calendar' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = filename;
                  link.click();
                  window.URL.revokeObjectURL(url);
                  
                  setSuccess(`Calendar downloaded successfully!`);
                } catch (error) {
                  setError('Failed to download calendar');
                } finally {
                  setDownloading(false);
                }
              }}
              fullWidth
              disabled={downloading || loading || (calendarScope === 'individual' && !selectedStaffMember)}
            >
              {calendarScope === 'all'
                ? 'Download calendar file (all staff)'
                : 'Download calendar file (selected staff)'}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Works with Apple Calendar, Outlook, and most other calendar apps.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarSyncSettings;

